/**
 * StatusBar — bottom persistent bar with active effect status + quick actions.
 * Inspired by OBS Studio's stats bar + Discord's in-call indicator.
 */

interface StatusBarProps {
  micActive:         boolean;
  swapping:          boolean;
  activeProfileName: string | null;
  activeVoiceName:   string | null;
  fps:               number;
  onStartCall?:      () => void;
  onOpenTopUp?:      () => void;
  isMobile?:         boolean;
}

import { useCloudCredits } from "@/hooks/useCloudCredits";

export function StatusBar({
  micActive, swapping, activeProfileName, activeVoiceName, fps,
  onOpenTopUp, isMobile = false,
}: StatusBarProps) {
  const { credits, minutesRemaining, status } = useCloudCredits();

  // Credit chip styles based on live balance
  const isDepleted = status === "depleted";
  const isLow = status === "low";

  const creditDotColor = isDepleted
    ? "var(--status-error)"
    : isLow
    ? "var(--status-warn)"
    : "var(--status-live)";

  const creditBorder = isDepleted
    ? "rgba(239, 68, 68, 0.4)"
    : isLow
    ? "rgba(245, 158, 11, 0.4)"
    : "rgba(0, 212, 170, 0.25)";

  const creditBg = isDepleted
    ? "rgba(239, 68, 68, 0.1)"
    : isLow
    ? "rgba(245, 158, 11, 0.1)"
    : "rgba(0, 212, 170, 0.08)";

  const creditTextColor = isDepleted
    ? "var(--status-error)"
    : isLow
    ? "var(--status-warn)"
    : "var(--text-primary)";

  const creditsLabel = credits !== null && minutesRemaining !== null
    ? `${minutesRemaining}m ($${credits.toFixed(2)})`
    : "Cloud Node Ready";

  return (
    <div style={{
      ...(isMobile ? { minHeight: "var(--bottom-bar-height)" } : { height: "var(--bottom-bar-height)" }),
      background: "var(--sidebar-bg)",
      borderTop: "1px solid var(--border-subtle)",
      display: "flex",
      alignItems: "center",
      padding: "0 var(--space-4)",
      gap: "var(--space-1)",
      flexShrink: 0,
      userSelect: "none",
    }}>
      {/* Active effects pills — hidden on mobile */}
      {!isMobile && (
        <>
          <StatusPill
            icon="◈"
            label={activeProfileName ? `Face: ${activeProfileName}` : "No face"}
            active={swapping}
            status={swapping ? "live" : activeProfileName ? "proc" : "idle"}
          />
          <StatusPill
            icon="◎"
            label="Voice: Studio"
            active={false}
            status="idle"
          />
          <StatusPill
            icon="⊚"
            label={micActive ? "Mic on" : "Mic off"}
            active={micActive}
            status={micActive ? "live" : "idle"}
          />
        </>
      )}

      {/* FPS Counter */}
      {swapping && fps > 0 && (
        <>
          <div style={{ width: 1, height: 14, background: "var(--border-subtle)", margin: "0 var(--space-2)" }} />
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            color: "var(--status-live)",
            fontWeight: "var(--weight-semibold)",
          }}>
            {fps} fps
          </span>
        </>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Cloud Node & Real-Time Credits Indicator */}
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        {/* RTX 5090 Blackwell Cloud Node Pill */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--accent)" }} />
          <span style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", fontWeight: 500 }}>
            RTX 5090 Blackwell Cloud Node
          </span>
        </div>

        <div style={{ width: 1, height: 14, background: "var(--border-subtle)" }} />

        {/* Real-time Credits & Remaining Minutes Chip */}
        <div
          onClick={onOpenTopUp}
          title={`StreamPersona Cloud Compute Rate: $6.58/hr (~$0.11/min)\nClick to top up minutes`}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "2px 8px",
            borderRadius: "var(--radius-full)",
            background: creditBg,
            border: `1px solid ${creditBorder}`,
            cursor: onOpenTopUp ? "pointer" : "default",
            transition: "all var(--duration-fast)",
          }}
        >
          <div style={{
            width: 6, height: 6, borderRadius: "50%",
            background: creditDotColor,
            animation: (isDepleted || isLow) ? "pulse-border 2s ease-in-out infinite" : "none",
          }} />
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            fontWeight: "var(--weight-semibold)",
            color: creditTextColor,
          }}>
            {creditsLabel}
          </span>
          {onOpenTopUp && (
            <span style={{
              fontSize: "10px",
              fontWeight: 700,
              color: "var(--accent)",
              padding: "1px 5px",
              background: "rgba(0, 212, 170, 0.15)",
              borderRadius: "4px",
              marginLeft: "2px",
            }}>
              + Top Up
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ icon, label, active, status }: {
  icon: string; label: string; active: boolean;
  status: "live" | "warn" | "error" | "proc" | "idle";
}) {
  const dotColor = {
    live: "var(--status-live)",
    warn: "var(--status-warn)",
    error: "var(--status-error)",
    proc: "var(--status-proc)",
    idle: "var(--status-idle)",
  }[status];

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-2)",
      fontSize: "var(--text-xs)",
      color: active ? "var(--text-secondary)" : "var(--text-muted)",
      padding: "0 var(--space-2)",
    }}>
      <div style={{
        width: 6, height: 6,
        borderRadius: "var(--radius-full)",
        background: dotColor,
        animation: status === "live" ? "pulse-border 2s ease-in-out infinite" : "none",
        flexShrink: 0,
      }} />
      <span className="truncate" style={{ maxWidth: 120 }}>{label}</span>
    </div>
  );
}

function Divider() {
  return (
    <div style={{
      width: 1, height: 14,
      background: "var(--border-subtle)",
      flexShrink: 0,
      margin: "0 var(--space-1)",
    }} />
  );
}
